import type { ViewportsConfigurations } from 'domains/viewer/ViewportsConfigurations';
import type {
  HangingProtocolPayload,
  ViewType,
  IViewportConfiguration,
  SeriesCriteriaCategory,
} from 'generated/graphql';
import uniq from 'ramda/es/uniq';
import { trim } from 'utils/string';
import { SERIES_CRITERIA_CATEGORY_MAP } from './seriesCriteriaCategoryMap';

/** Mutable and non-nullable version of IViewportConfiguration */
export type MutableViewportConfig = {
  id: string;
  viewType: ViewType;
  comparisonIndex: number | null;
  seriesId: string | undefined;
  seriesDescriptions: string[];
  viewportMirrorGroupId?: number;
  criteria?: ReadonlyArray<{ category: SeriesCriteriaCategory; value: string }>;
};

type MirrorGroupKeyType = `${'series' | 'criteria'}|${string}|${string}`;

/**
 * Everything mirror grouping reads about a viewport, and nothing else.
 *
 * `criteria.category` is a plain string rather than `SeriesCriteriaCategory` because the two callers
 * disagree on its type -- the flat path holds the enum, `StepViewportCriterionInput` declares
 * `String` -- and the grouping key only ever compares categories as text. Naming the narrow shape
 * lets both pass their own criteria through untouched instead of casting one into the other's type.
 */
type MirrorGroupInput = {
  id: string;
  comparisonIndex: number | null;
  seriesId: string | undefined;
  criteria?: ReadonlyArray<{ category: string; value: string }>;
};

/**
 * The key that decides mirror-group membership, in two tiers.
 *
 * A viewport that resolved to a series keys on that series **and its classification**. The series is
 * the only evidence that separates "the user hung one series twice" (VX-5006, group wanted) from "the
 * user hung two series that happen to classify alike" (VX-5005, group not wanted), which criteria
 * cannot: the form classifies by series description and a study routinely carries two series under one
 * description.
 *
 * The classification has to be in the key as well, because it is not a property of the series -- it is
 * derived from the description in the viewport's own field, and editing that description changes it.
 * So two viewports can hold one series and still carry different rules, and they are then not
 * mirrors: a mirror group tells the backend to hang one shared stack in all its members, which
 * discards whichever rule did not win. Keying on the series alone merged them and silently dropped
 * the edit. Same series *and* same classification is the condition for "these two say the same
 * thing".
 *
 * That the two are usually equal is what makes the addition safe for VX-5006: a series dropped into
 * two viewports classifies identically in both (same description, and since VX-4970 the same series
 * record), so the pair still groups. Where they diverge, the divergence is the author's edit.
 *
 * A viewport that resolved to nothing has no series evidence, so it keys on its criteria alone. That is
 * what lets a protocol authored with nothing hung still record the mirroring its author asked for.
 *
 * The tier is part of the key, so the two spaces never collide. An unresolved viewport cannot join a
 * resolved viewport's group on matching classification alone -- that collapse is exactly VX-5005, and
 * the resolved side of such a pair has already been told, by its own series, that it does not belong.
 * Within the unresolved tier two viewports the author meant to hold different series will still merge;
 * there is nothing left to tell them apart, and their criteria are the only intent on record.
 *
 * Returns `null` for a viewport with neither a series nor criteria: no evidence of mirroring anything.
 *
 * `null` (current exam) and `0` (first prior) must remain distinct comparison keys.
 */
/**
 * A viewport's classification as a stable, order-independent string.
 *
 * JSON rather than a joined string: a delimiter would have to be a character neither a category nor
 * an ontology id can contain, and the one that qualifies (NUL) turns any file quoting it binary
 * (b75c3f3cd4). Sorted because criteria are a set -- the same pairs in a different order are the same
 * classification and have to produce one key.
 */
function classificationSignature(criteria: MirrorGroupInput['criteria']): string {
  return JSON.stringify(
    (criteria ?? [])
      .map(({ category, value }) => [category, value] as const)
      .sort(([aCategory, aValue], [bCategory, bValue]) =>
        aCategory === bCategory ? aValue.localeCompare(bValue) : aCategory.localeCompare(bCategory)
      )
  );
}

function createMirrorGroupKey(viewport: MirrorGroupInput): MirrorGroupKeyType | null {
  const comparison = viewport.comparisonIndex === null ? 'current' : `${viewport.comparisonIndex}`;
  const signature = classificationSignature(viewport.criteria);

  if (viewport.seriesId) {
    return `series|${viewport.seriesId}|${signature}|${comparison}`;
  }

  if ((viewport.criteria ?? []).length === 0) {
    return null;
  }

  return `criteria|${signature}|${comparison}`;
}

/**
 * The mirror group id for each viewport, in order, `null` where the viewport belongs to no group.
 *
 * Algorithm:
 * 1. Group viewports by {@link createMirrorGroupKey} -- their resolved series where they have one,
 *    their criteria where they do not, always within one comparison context
 * 2. For groups with 2+ viewports, assign a shared mirror group ID (1-100)
 * 3. A viewport alone under its key, or with no key at all, gets `null`
 *
 * @param viewports - Viewports sorted by layout order, which fixes the order group ids are handed out
 */
function computeMirrorGroupIds(viewports: ReadonlyArray<MirrorGroupInput>): Array<number | null> {
  const viewportIdsByKey = new Map<MirrorGroupKeyType, string[]>();

  for (const viewport of viewports) {
    const groupKey = createMirrorGroupKey(viewport);
    if (groupKey === null) {
      continue;
    }
    const existing = viewportIdsByKey.get(groupKey) ?? [];
    existing.push(viewport.id);
    viewportIdsByKey.set(groupKey, existing);
  }

  let nextMirrorGroupId = 1;
  const mirrorGroupIdByViewportId = new Map<string, number>();

  for (const viewportIds of viewportIdsByKey.values()) {
    if (viewportIds.length <= 1) {
      continue;
    }

    if (nextMirrorGroupId > 100) {
      throw new Error(
        'Cannot create more than 100 mirror groups in a hanging protocol. This limit has been exceeded.'
      );
    }

    const mirrorGroupId = nextMirrorGroupId++;
    for (const viewportId of viewportIds) {
      mirrorGroupIdByViewportId.set(viewportId, mirrorGroupId);
    }
  }

  return viewports.map((viewport) => mirrorGroupIdByViewportId.get(viewport.id) ?? null);
}

/**
 * Processes viewports to identify mirror groups and apply mirror group IDs.
 * Mirror groups are viewports that display the same series in the same comparison context.
 *
 * The first viewport (by layout order) is the source for shared data, and all viewports are kept in
 * the output -- a non-member is returned exactly as it came in.
 *
 * @param viewportConfigs - Array of viewport configurations sorted by layout order
 * @returns Array of viewport configurations with mirror groups applied (all viewports retained)
 */
export const applyMirrorGroups = (
  viewportConfigs: MutableViewportConfig[]
): MutableViewportConfig[] => {
  const mirrorGroupIds = computeMirrorGroupIds(viewportConfigs);

  return viewportConfigs.map((viewport, index) => {
    const mirrorGroupId = mirrorGroupIds[index];

    if (mirrorGroupId !== null) {
      return {
        ...viewport,
        viewportMirrorGroupId: mirrorGroupId,
      };
    }

    // Non-mirror viewport: return as-is
    return viewport;
  });
};

/**
 * Which series each viewport is actually showing, keyed by viewport id.
 *
 * This outranks everything else in deciding mirror-group membership. A hanging protocol is stored
 * criteria-only, and two viewports that hold genuinely different series routinely store byte-identical
 * criteria -- the form classifies by series description, so two series sharing a description classify
 * the same (VX-5005). Grouping on criteria therefore cannot tell "the user hung one series twice"
 * (VX-5006, group wanted) from "the user hung two series that happen to classify alike" (VX-5005,
 * group not wanted). The viewer's own viewport assignments can, and they are what the user was looking
 * at when they pressed Save.
 *
 * A viewport this map cannot answer for falls back to its criteria, which is the weaker evidence this
 * type exists to outrank -- see {@link createMirrorGroupKey} for why that cannot merge the two.
 *
 * `undefined` for a viewport means "not known", which is different from "known to be nothing": callers
 * treat an all-unknown set as "do not recompute" rather than as "clear every group".
 */
export type ViewportSeriesIds = Readonly<Record<string, string | undefined>>;

/** True when nothing in the map resolves -- the signal to leave existing mirror groups alone. */
export function hasNoResolvedSeries(seriesIds: Nullable<ViewportSeriesIds>): boolean {
  return seriesIds == null || Object.values(seriesIds).every((smid) => smid == null);
}

/**
 * Assigns mirror group ids to viewports by the series they hold, falling back to their criteria where
 * no series resolved, and returning one id (or `null`) per entry, in order.
 *
 * A viewport with neither is never grouped: nothing about it says it mirrors anything. See
 * {@link createMirrorGroupKey} for why the two tiers cannot be collapsed into one.
 */
export function assignMirrorGroupIds(
  entries: ReadonlyArray<Omit<MirrorGroupInput, 'seriesId'>>,
  seriesIds: ViewportSeriesIds
): Array<number | null> {
  return computeMirrorGroupIds(
    entries.map((entry) => ({ ...entry, seriesId: seriesIds[entry.id] }))
  );
}

export const viewportsConfigToHangingProtocol = (
  viewportsConfigurations: ViewportsConfigurations
): HangingProtocolPayload['data']['viewportsConfigurations'] => {
  // First pass: collect all viewport configs
  const viewportConfigs = Object.entries(viewportsConfigurations).reduce<MutableViewportConfig[]>(
    (acc, [key, config]) => {
      if (config == null) {
        return acc;
      }

      const {
        series,
        seriesDescriptions = [],
        viewType,
        comparisonIndex = null,
        stack,
        seriesCriteria,
      } = config;

      // Transform seriesCriteria to criteria array format for the GraphQL input
      const criteria = seriesCriteria
        ? Object.entries(seriesCriteria).flatMap(([key, values]) => {
            const category =
              SERIES_CRITERIA_CATEGORY_MAP[key as keyof typeof SERIES_CRITERIA_CATEGORY_MAP];
            if (!category || !Array.isArray(values)) return [];
            return values
              .filter((v) => v.ontology_id)
              .map((v) => ({ category, value: v.ontology_id as string }));
          })
        : undefined;
      // There are a couple edge cases we want to handle, here:
      // 1. The viewport is "blank" and does not have a series
      // 2. The series does not have a description
      // For both of these cases, we will either set the existing [...seriesDescriptions] or provide a default [].
      const isValidDescription = series?.description != null && series?.description !== '';

      // The stack's frame data is more specific and authoritative about what series is actually
      // being shown in that viewport. However, not all viewports have stacks (MPRs or other view
      // types that don't use the stack structure). In those cases, we'll use the viewport's series
      // reference.
      const seriesId = stack?.frames?.[0].series.smid ?? series?.smid;

      return [
        ...acc,
        {
          id: key,
          // ViewportTypeKeys and ViewType represent the same values, cast for compatibility
          viewType: viewType as ViewType,
          comparisonIndex,
          seriesId,
          seriesDescriptions: uniq(
            isValidDescription
              ? [...seriesDescriptions, trim(series?.description)]
              : seriesDescriptions
          ),
          ...(criteria && criteria.length > 0 ? { criteria } : {}),
        },
      ];
    },
    []
  );

  // Sort viewports by layout order (ID determines position)
  // This ensures a consistent "first member" selection for each mirror group
  viewportConfigs.sort((a, b) => a.id.localeCompare(b.id));

  // Apply mirror group logic: identify groups and assign IDs
  // The cast below is required because we are transforming from a ViewportConfig
  // (mutable/non-nullable) to an IViewportConfiguration (readonly/nullable, GraphQL-compatible).
  // They are structurally the same at runtime.
  return applyMirrorGroups(viewportConfigs) as IViewportConfiguration[];
};
